// src/pages/ExperienceCatalog.tsx

import { RiArrowLeftSLine } from 'react-icons/ri';
import { Link, useNavigate } from 'react-router-dom';

import heroPhone from 'assets/img/experienceCatalog/hero-phone.png';
import idea from 'assets/img/experienceCatalog/idea.png';
import qualityLife from 'assets/img/experienceCatalog/quality-of-life.png';
import smiley from 'assets/img/experienceCatalog/smiley.png';
import { useCallback, useEffect, useState } from 'react';
import ArticlePlaceholder from 'components/ui/article-placeholder';
import { ExperienceCatalogResponse, PremiacaoIsolada, Combo } from 'types/experienceCatalog';
import {
  fetchExperienceCatalogList,
  getDeadLineExperienceCatalog,
  purchaseItemExperienceCatalog,
  loadExperiencePurchase,
  getRegulationExperienceCatalog,
} from 'services/experienceCataolog.service';
import ExperienceSelectCard from './ExperienceSelectCard';
import { useAuth } from 'context/AuthContext';
import { Countdown } from 'components/countdown';
import ProgressBar from 'components/ui/progress-bar';

export default function ExperienceCatalog() {
  const { userInfo } = useAuth();
  const [catalogData, setCatalogData] = useState<ExperienceCatalogResponse>();
  const [deadLine, setDeadLine] = useState<Date>(new Date());
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [loading, setLoading] = useState(true);
  const [ruleUrl, setRuleUrl] = useState('');

  const navigate = useNavigate();

  const verifyExperience = useCallback(async () => {
    try {
      if (!userInfo || !userInfo.chave) {
        return;
      }
      const response = await loadExperiencePurchase(userInfo.chave, '1S2025');
      // if (response) {
      //   navigate('feedback');
      // }
    } catch (error) {
      console.error('Error verifying experience:', error);
    }
  }, [userInfo, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!userInfo || !userInfo.chave || !userInfo?.piloto_catalogo) {
        setLoading(false);
        return;
      }
      try {
        const result = await fetchExperienceCatalogList(userInfo.chave, '1S2025');
        const deadLineResponse = await getDeadLineExperienceCatalog();
        const rule = await getRegulationExperienceCatalog();

        if (rule) {
          setRuleUrl(rule.file?.url || '');
        }

        let endDate;
        if (deadLineResponse.endDate?.$date) {
          endDate = new Date(deadLineResponse.endDate.$date);
        } else if (typeof deadLineResponse.endDate === 'string') {
          endDate = new Date(deadLineResponse.endDate);
        } else if (deadLineResponse.endDate instanceof Date) {
          endDate = deadLineResponse.endDate;
        } else {
          console.error('Invalid endDate format:', deadLineResponse.endDate);
          endDate = new Date();
        }
        setDeadLine(endDate);

        if(endDate < new Date()) {
          navigate('feedback');
        }

        let startDateValue;
        if (deadLineResponse.startDate?.$date) {
          startDateValue = new Date(deadLineResponse.startDate.$date);
        } else if (deadLineResponse.created?.$date) {
          startDateValue = new Date(deadLineResponse.created.$date);
        } else if (typeof deadLineResponse.startDate === 'string') {
          startDateValue = new Date(deadLineResponse.startDate);
        } else if (deadLineResponse.startDate instanceof Date) {
          startDateValue = deadLineResponse.startDate;
        } else {
          console.error('Invalid startDate format:', deadLineResponse.startDate);
          startDateValue = new Date();
        }
        setStartDate(startDateValue);

        setCatalogData(result);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    verifyExperience();
  }, [userInfo, navigate, verifyExperience]);

  const purchaseExperience = async (item: PremiacaoIsolada | Combo) => {
    try {
      if (!userInfo || !userInfo.chave) {
        return;
      }
      const response = await purchaseItemExperienceCatalog(item, userInfo.chave);
      navigate('feedback');
    } catch (error) {
      console.error('Error purchasing experience:', error);
    }
  };

  return (
    <div className="text-white min-h-screen">
      {/* Skip Link para navegação rápida */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-yellow text-oxford-blue px-4 py-2 rounded-md font-bold z-50 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2"
      >
        Pular para conteúdo principal
      </a>

      {loading ? (
        <div
          className="text-center text-white flex flex-col items-center justify-center min-h-screen"
          role="status"
          aria-live="polite"
          aria-label="Carregando catálogo de experiências"
        >
          <ArticlePlaceholder />
          <ArticlePlaceholder />
          <span className="sr-only">Carregando informações do catálogo de experiências...</span>
        </div>
      ) : (
        <>
          <header className="flex flex-col items-start py-10 container mx-auto lg:p-10">
            <div className="flex flex-row mb-4 justify-center items-center">
              <div className="gap-4">
                <Link to="../" className="focus:outline-none">
                  <button
                    className="rounded-full bg-purple flex justify-center items-center p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-purple transition-all"
                    aria-label="Voltar para a página anterior"
                  >
                    <RiArrowLeftSLine size={28} className="text-white" />
                  </button>
                </Link>
              </div>
              <h1
                className="text-lg lg:text-3xl py-1 px-6 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 transition-all"
                tabIndex={0}
                role="banner"
                aria-label="Página principal: Catálogos de Experiências"
              >
                Catálogos de Experiências
              </h1>
            </div>
          </header>

          <section aria-labelledby="hero-heading">
            <div className="bg-maastricht-blue flex flex-col md:flex-row items-center py-10 px-4">
              <div className="flex flex-row items-center container mx-auto">
                <div className="lg:px-10">
                  <h2
                    id="hero-heading"
                    className="text-4xl font-semibold text-electric-blue mb-4 max-w-2xl relative focus:outline-none focus-visible:ring-2 focus-visible:ring-electric-blue focus-visible:ring-offset-2 focus-visible:ring-offset-maastricht-blue transition-all"
                    tabIndex={0}
                  >
                    Viva o que importa!
                  </h2>
                  <p
                    className="max-w-2xl text-2xl text-center md:text-left mb-6 font-extralight focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-maastricht-blue transition-all"
                    tabIndex={0}
                    aria-label="Descrição do Catálogo de Experiências: O Catálogo de Experiências é um convite para criar memórias e escolher momentos que fazem sentido para você. Seja para celebrar, presentear ou viver algo novo."
                  >
                    O <b>Catálogo de Experiências</b> é um convite pra criar memórias e escolher momentos que fazem sentido pra
                    você. Seja para celebrar, presentear ou viver algo novo.
                  </p>
                </div>
                <img
                  src={heroPhone}
                  alt="Telefone celular mostrando a interface do aplicativo do Catálogo de Experiências com opções de experiências disponíveis"
                  className="w-[300px] md:w-[520px] lg:w-[600px] right-[0%] 2xl:right-[17%] absolute hidden md:block"
                  aria-describedby="hero-phone-desc"
                />
                <span id="hero-phone-desc" className="sr-only">
                  Imagem ilustrativa mostrando um smartphone com a tela do aplicativo do Catálogo de Experiências, demonstrando as
                  funcionalidades disponíveis para os usuários
                </span>
              </div>
            </div>
          </section>

          <section className="bg-slate-blue py-10 px-4" aria-labelledby="benefits-heading">
            <div className="flex flex-col items-start justify-start container mx-auto lg:px-10">
              <h3
                id="benefits-heading"
                className="text-4xl font-bold mb-14 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-slate-blue transition-all"
                tabIndex={0}
              >
                Porque a vida é feita
                <br /> de experiências!
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto text-xl font-extralight">
                <div
                  aria-label="Benefício 1: Experiências enriquecem a vida e significam investir em si mesmo."
                  className="flex flex-col items-center border-r-2 border-white pr-16 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-slate-blue transition-all rounded-lg p-4"
                  tabIndex={0}
                >
                  <img
                    src={qualityLife}
                    alt="Ícone de qualidade de vida mostrando uma pessoa em posição de meditação, representando bem-estar e autocuidado"
                    className="w-[80px] mb-4"
                  />
                  <p className="text-center font-medium">Experiências enriquecem a vida e significam investir em si mesmo.</p>
                </div>
                <div
                  className="flex flex-col items-center border-r-2 border-white pr-16 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-slate-blue transition-all rounded-lg p-4"
                  tabIndex={0}
                >
                  <img
                    src={idea}
                    alt="Ícone de inspiração mostrando uma lâmpada acesa, simbolizando ideias e criatividade"
                    className="w-[80px] mb-4"
                  />
                  <p className="text-center font-medium">Inspiram novas formas de ver o mundo e viver a vida.</p>
                </div>
                <div
                  className="flex flex-col items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-slate-blue transition-all rounded-lg p-4"
                  tabIndex={0}
                  aria-label="Benefício 3: Experiências criam memórias duradouras e expandem os horizontes."
                >
                  <img
                    src={smiley}
                    alt="Ícone de felicidade mostrando um emoji sorridente, representando alegria e satisfação"
                    className="w-[80px] mb-4"
                  />
                  <p className="text-center font-medium">
                    Criam memórias <br />
                    duradouras e expandem os horizontes.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="container mx-auto py-8 px-10" aria-labelledby="deadline-section">
            <div
              className="relative border-2 border-electric-blue bg-oxford-blue rounded-2xl lg:rounded-[20px] px-6 lg:px-8 py-8 flex flex-col space-y-4 2xl:mr-[152px] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 transition-all"
              tabIndex={0}
            >
              <p
                id="deadline-section"
                className="text-center mb-2 flex focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-oxford-blue transition-all text-white font-medium"
                tabIndex={-1}
              >
                A sua escolha deve ser finalizada em:{' '}
                <span
                  className="text-yellow flex font-bold ml-2"
                  role="timer"
                  aria-label="Contador de tempo restante para escolha da experiência"
                >
                  {deadLine && !isNaN(deadLine.getTime()) ? (
                    <>
                      <Countdown deadline={deadLine} />
                    </>
                  ) : (
                    <span aria-label="Carregando informação do prazo">Carregando...</span>
                  )}
                </span>
              </p>
              <div className="mt-2">
                <div role="progressbar" aria-label="Progresso do tempo restante para escolha da experiência" tabIndex={-1}>
                  <ProgressBar startDate={startDate.getTime()} endDate={deadLine.getTime()} />
                </div>
                <p
                  className="text-xs mt-2 font-light focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-oxford-blue transition-all"
                  tabIndex={-1}
                  role="note"
                >
                  Encerrado o prazo de escolha, a concessão de pontos Livelo será realizada automaticamente para quem não tiver
                  registrado uma opção.
                </p>
              </div>
            </div>
          </section>

          {catalogData?.premiacaoIsolada || catalogData?.combos ? (
            <main id="main-content" className="container px-10 mx-auto pb-12" role="main" aria-labelledby="catalog-heading">
              <h2 id="catalog-heading" className="sr-only">
                Experiências disponíveis para seleção
              </h2>
              <div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 2xl:mr-36"
                role="list"
                aria-label={`${(catalogData?.premiacaoIsolada?.length || 0) + (catalogData?.combos?.length || 0)} experiências disponíveis`}
              >
                {/* Premiações Isoladas */}
                {catalogData?.premiacaoIsolada
                  ?.slice()
                  .sort((a, b) => (a.type === 'pontos_Livelo' ? -1 : b.type === 'pontos_Livelo' ? 1 : 0))
                  .map((item, index) => (
                    <div key={item.itemIdPurchase} role="listitem">
                      <ExperienceSelectCard
                        item={item}
                        validity={deadLine}
                        type="unico"
                        purchaseExperience={purchaseExperience}
                        ruleUrl={ruleUrl}
                      />
                    </div>
                  ))}

                {/* Combos */}
                {catalogData?.combos?.map((item, index) => (
                  <div key={item.itemIdPurchase} role="listitem">
                    <ExperienceSelectCard
                      item={item}
                      validity={deadLine}
                      type="combo"
                      purchaseExperience={purchaseExperience}
                      ruleUrl={ruleUrl}
                    />
                  </div>
                ))}
              </div>
            </main>
          ) : (
            <section className="container px-10 mx-auto pb-12" role="alert" aria-labelledby="no-catalog-heading">
              <div
                className="relative border-2 border-electric-blue bg-oxford-blue rounded-2xl lg:rounded-[20px] px-6 lg:px-8 py-8 flex flex-col space-y-4 2xl:mr-[152px] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 transition-all"
                tabIndex={0}
              >
                <h2 id="no-catalog-heading" className="sr-only">
                  Informações sobre acesso ao catálogo
                </h2>
                <p
                  className="text-center mb-2 text-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-oxford-blue transition-all"
                  role="status"
                  aria-live="polite"
                  tabIndex={0}
                >
                  Colega, conforme regulamento do piloto, para acesso ao Catálogo de Experiências é necessário ter adquirido pelo
                  menos 10.000 pontos. Mas não se preocupe, mesmo não habilitando a opção do catálogo, você receberá seus pontos
                  Livelo normalmente, desde que cumpridos os requisitos previstos na IN 403.
                </p>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
